import { useCallback, useMemo, useRef } from 'react';

import { useProject } from 'store/project';
import { useCurrentLocale } from 'store/ui';
import { pushTokenOnResume } from 'services/auth/tokenServer';
import { useAuth0 } from 'services/auth0/react-auth0.spa';
import { notifyServiceWorkerConfigSet } from 'services/serviceWorker/swConfigReady';
import { logChannelRequestFailure } from 'setup/logger/logChannelRequestFailure';
import { serviceWorkerConfigClient } from 'shared/domain/serviceWorkerConfig/serviceWorkerConfigClient';
import {
  ServiceWorkerSelection,
  toServiceWorkerConfigInput,
} from 'shared/domain/serviceWorkerConfig/serviceWorkerConfigModel';
import { fingerprintString } from 'shared/utils/hash';
import { useSyncConfigWhenAuthReady } from './hooks';

type ServiceWorkerConfig = ServiceWorkerSelection & { token: string };

const createConfigKey = ({ locale, token, projectId, organizationId, timezone }: ServiceWorkerConfig): string =>
  [locale, fingerprintString(token), projectId ?? '', organizationId ?? '', timezone ?? ''].join('|');

/**
 * Sends the config to the worker; resolves whether it stored it. The token
 * goes with it as a push, so a restarted worker does not have to ask for it.
 */
async function sendServiceWorkerConfig(config: ServiceWorkerConfig): Promise<boolean> {
  const input = toServiceWorkerConfigInput(config);
  if (!input) {
    return false;
  }
  void pushTokenOnResume();
  try {
    await serviceWorkerConfigClient.set(input);
    return true;
  } catch (error) {
    logChannelRequestFailure('Could not configure the service worker', error);
    return false;
  }
}

/** Keeps the worker configured for the signed-in user's locale and project. */
export function useSetServiceWorkerConfig(): void {
  const { token, isAuthenticated, loading } = useAuth0();
  const locale = useCurrentLocale();
  const { _id: projectId, organizationId, timezone } = useProject();
  const sentConfigKeyRef = useRef<string | null>(null);

  const config = useMemo<ServiceWorkerConfig | null>(
    () => (token && locale ? { locale, token, projectId, organizationId, timezone } : null),
    [locale, token, projectId, organizationId, timezone]
  );

  const syncConfig = useCallback((nextConfig: ServiceWorkerConfig) => {
    const configKey = createConfigKey(nextConfig);
    if (sentConfigKeyRef.current === configKey) {
      return;
    }
    sentConfigKeyRef.current = configKey;

    void sendServiceWorkerConfig(nextConfig).then((stored) => {
      // A newer config was sent meanwhile: only its answer counts.
      if (sentConfigKeyRef.current !== configKey) {
        return;
      }
      if (stored) {
        notifyServiceWorkerConfigSet();
      } else {
        // Not stored: the next change of the config tries again.
        sentConfigKeyRef.current = null;
      }
    });
  }, []);

  useSyncConfigWhenAuthReady({ config, loading, isAuthenticated, syncConfig });
}
